import { useEffect, useState, type FormEvent } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { Eye, EyeOff, ImageUp, Pencil, Plus, Trash2, UsersRound } from 'lucide-react'
import Modal from './Modal'
import { boardMemberImage, type BoardMember } from '../types/Board'

interface UserOption {
  id: number
  firstName: string
  lastName: string
  profileImage: string | null
}

interface BoardForm {
  userId: string
  role: string
  photoUrl: string
  displayOrder: number
  isVisible: boolean
}

const emptyForm = (position = 1): BoardForm => ({ userId: '', role: '', photoUrl: '', displayOrder: position, isVisible: true })

const errorMessage = (error: unknown, fallback: string) => axios.isAxiosError(error)
  ? error.response?.data?.message || fallback
  : fallback

const BoardAdminModule = () => {
  const [members, setMembers] = useState<BoardMember[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<BoardMember | null>(null)
  const [form, setForm] = useState<BoardForm>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const loadData = async () => {
    try {
      const [{ data: board }, { data: memberList }] = await Promise.all([
        axios.get<BoardMember[]>('/api/board/admin'),
        axios.get<UserOption[]>('/api/users/admin'),
      ])
      setMembers(board)
      setUsers(memberList)
    } catch (error) {
      toast.error(errorMessage(error, 'Impossible de charger le bureau'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void loadData() }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm(members.length + 1))
    setShowForm(true)
  }

  const openEdit = (member: BoardMember) => {
    setEditing(member)
    setForm({ userId: String(member.userId), role: member.role, photoUrl: member.photoUrl || '', displayOrder: member.displayOrder, isVisible: member.isVisible })
    setShowForm(true)
  }

  const deleteOrphan = async (path: string) => {
    try {
      await axios.delete('/api/upload', { data: { path } })
    } catch (error) {
      if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) console.error('Nettoyage de la photo du bureau impossible :', error)
    }
  }

  const closeForm = async () => {
    if (saving || uploading) return
    if (form.photoUrl && form.photoUrl !== editing?.photoUrl) await deleteOrphan(form.photoUrl)
    setShowForm(false)
  }

  const uploadPhoto = async (file: File) => {
    setUploading(true)
    try {
      const payload = new FormData()
      payload.append('boardImage', file)
      const { data } = await axios.post<{ imageUrl: string }>('/api/upload/board', payload, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (form.photoUrl && form.photoUrl !== editing?.photoUrl) await deleteOrphan(form.photoUrl)
      setForm((current) => ({ ...current, photoUrl: data.imageUrl }))
      toast.success('Photo ajoutée')
    } catch (error) {
      toast.error(errorMessage(error, 'Impossible d’envoyer la photo'))
    } finally {
      setUploading(false)
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    try {
      const payload = { ...form, userId: Number(form.userId), photoUrl: form.photoUrl || null }
      if (editing) await axios.put(`/api/board/${editing.id}`, payload)
      else await axios.post('/api/board', payload)
      toast.success(editing ? 'Poste mis à jour' : 'Poste ajouté')
      setShowForm(false)
      await loadData()
    } catch (error) {
      toast.error(errorMessage(error, 'Impossible d’enregistrer ce poste'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (member: BoardMember) => {
    if (!window.confirm(`Supprimer le poste « ${member.role} » ?`)) return
    try {
      await axios.delete(`/api/board/${member.id}`)
      toast.success('Poste supprimé')
      await loadData()
    } catch (error) {
      toast.error(errorMessage(error, 'Impossible de supprimer ce poste'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="admin-module-toolbar">
        <p className="text-sm text-gray-600">Composez la page publique du bureau et choisissez l’ordre des postes.</p>
        <button type="button" className="btn-primary" onClick={openCreate}><Plus className="h-4 w-4" /> Ajouter un poste</button>
      </div>

      {loading ? <div className="board-status">Chargement…</div> : members.length === 0 ? (
        <div className="empty-state"><UsersRound className="mx-auto mb-4 h-10 w-10 text-emerald-600" /><h3>Aucun poste configuré</h3><p className="mt-2 text-gray-600">Ajoutez les membres du bureau pour publier la page.</p></div>
      ) : (
        <div className="board-admin-list">
          {members.map((member) => {
            const image = boardMemberImage(member)
            return (
              <article className="board-admin-row" key={member.id}>
                {image ? <img src={image} alt="" /> : <span className="board-admin-avatar">{member.user.firstName.charAt(0)}{member.user.lastName.charAt(0)}</span>}
                <div className="min-w-0 flex-1"><small>Position {member.displayOrder}</small><h3>{member.role}</h3><p>{member.user.firstName} {member.user.lastName}</p></div>
                <span className={`board-visibility ${member.isVisible ? '' : 'board-visibility-hidden'}`}>{member.isVisible ? <Eye /> : <EyeOff />}{member.isVisible ? 'Public' : 'Masqué'}</span>
                <div className="flex gap-2"><button type="button" className="btn-secondary" onClick={() => openEdit(member)} title="Modifier"><Pencil className="h-4 w-4" /></button><button type="button" className="btn-danger" onClick={() => void remove(member)} title="Supprimer"><Trash2 className="h-4 w-4" /></button></div>
              </article>
            )
          })}
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => void closeForm()} title={editing ? 'Modifier le poste' : 'Ajouter un poste'} eyebrow="Bureau du club" footer={<><button type="button" className="btn-secondary" onClick={() => void closeForm()} disabled={saving || uploading}>Annuler</button><button type="submit" form="board-form" className="btn-primary" disabled={saving || uploading}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button></>}>
        <form id="board-form" className="space-y-5" onSubmit={save}>
          <label className="form-field"><span className="form-label">Adhérent</span><select className="input-field" required value={form.userId} onChange={(event) => setForm({ ...form, userId: event.target.value })}><option value="">Sélectionner un adhérent</option>{users.map((user) => <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>)}</select></label>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="form-field"><span className="form-label">Fonction</span><input className="input-field" required minLength={2} maxLength={100} placeholder="Présidente, Trésorier…" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} /></label>
            <label className="form-field"><span className="form-label">Position</span><input className="input-field" type="number" required min={0} value={form.displayOrder} onChange={(event) => setForm({ ...form, displayOrder: Number(event.target.value) })} /></label>
          </div>
          <section className="form-section">
            <div className="form-section-heading"><div><h3>Photo dédiée</h3><p>Facultative. Sans photo, celle du profil de l’adhérent sera utilisée.</p></div></div>
            <div className="board-photo-field">
              {form.photoUrl ? <img src={form.photoUrl} alt="Aperçu" /> : <ImageUp aria-hidden="true" />}
              <label className="btn-secondary cursor-pointer"><ImageUp className="h-4 w-4" />{uploading ? 'Envoi…' : 'Choisir une photo'}<input className="sr-only" type="file" accept="image/jpeg,image/png,image/gif,image/webp" disabled={uploading} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void uploadPhoto(file) }} /></label>
              {form.photoUrl && <button type="button" className="btn-secondary" onClick={() => setForm({ ...form, photoUrl: '' })}>Utiliser le profil</button>}
            </div>
          </section>
          <label className="flex items-center gap-3"><input type="checkbox" checked={form.isVisible} onChange={(event) => setForm({ ...form, isVisible: event.target.checked })} /><span><strong>Afficher sur la page publique</strong><small className="block text-gray-500">Vous pouvez préparer un poste avant de le rendre visible.</small></span></label>
        </form>
      </Modal>
    </div>
  )
}

export default BoardAdminModule
